import { themePresets } from '@/config/themePresets';

/**
 * Converts a HEX color string to HSL values.
 *
 * @param {string} hex - HEX color string (e.g. '#F93E3E')
 * @returns {{ h: number, s: number, l: number } | null}
 */
function hexToHsl(hex) {
  if (!hex || typeof hex !== 'string') return null;

  const clean = hex.replace('#', '');
  if (clean.length !== 6) return null;

  const r = parseInt(clean.slice(0, 2), 16) / 255;
  const g = parseInt(clean.slice(2, 4), 16) / 255;
  const b = parseInt(clean.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;

  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (delta !== 0) {
    s = delta / (1 - Math.abs(2 * l - 1));

    if (max === r) {
      h = ((g - b) / delta) % 6;
    } else if (max === g) {
      h = (b - r) / delta + 2;
    } else {
      h = (r - g) / delta + 4;
    }
    h = Math.round(h * 60);
    if (h < 0) h += 360;
  }

  return { h, s: s * 100, l: l * 100 };
}

/**
 * Maps a HEX color to a default image ID based on HSL hue detection.
 *
 * @param {string} hex - Primary button HEX color string
 * @returns {string} Default image ID
 */
function resolveDefaultImageIdFromColor(hex) {
  const hsl = hexToHsl(hex);
  if (!hsl) return 'classic_red';

  const { h, s } = hsl;

  if (s < 15) return 'slate_modern';
  if (h >= 330 || h <= 20) return 'classic_red';
  if (h > 20 && h <= 70) return 'amber_luxury';
  if (h > 70 && h <= 165) return 'forest_executive';
  if (h > 165 && h <= 260) return 'navy_professional';
  if (h > 260 && h <= 290) return 'slate_modern';
  if (h > 290 && h <= 329) return 'violet_creative';

  return 'classic_red';
}

/**
 * Resolves the recommended default image ID matching the active tenant theme.
 *
 * Checks exact theme preset match first. If custom colors are used, falls back
 * to hue detection on the primary button color.
 *
 * @param {object|null} themeCorporate Active corporate theme object from state
 * @returns {string|null} Recommended default image ID or null if unresolvable
 */
export function resolveRecommendedDefaultImageId(themeCorporate) {
  if (!themeCorporate) return null;

  const matchedPreset = themePresets.find((preset) =>
    Object.keys(preset.colors).every(
      (key) => preset.colors[key]?.toLowerCase() === themeCorporate[key]?.toLowerCase()
    )
  );

  if (matchedPreset) {
    return matchedPreset.id;
  }

  const primaryButton = themeCorporate.primary_button;
  if (!primaryButton) return null;

  return resolveDefaultImageIdFromColor(primaryButton);
}
